import { useEffect, useRef, useState, useCallback } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "./RegionMap.css";
import baseMapStyle from "./mapStyle.json";

const fmtGBP = (n) => "£ " + Math.round(n).toLocaleString("en-GB");

// Ported from region-osm-view.component.ts's createFeatureFromItem() -
// our Flask route returns snake_case (region_name, region_id, full_l1,
// full_l2, full_l3, latitude, longitude) matching territory_region_source.sql's
// column names directly, unlike the Angular version's camelCase.
function toFeature(item) {
  const lng = parseFloat(item.longitude);
  const lat = parseFloat(item.latitude);
  if (Number.isNaN(lng) || Number.isNaN(lat)) return null;
  return {
    type: "Feature",
    geometry: { type: "Point", coordinates: [lng, lat] },
    properties: {
      regionName: item.region_name,
      regionId: item.region_id,
      fullL1: item.full_l1,
      fullL2: item.full_l2,
      fullL3: item.full_l3,
    },
  };
}

const METRIC_OPTIONS = [
  { label: "L1", value: "fullL1" },
  { label: "L2", value: "fullL2" },
  { label: "L3", value: "fullL3" },
];

// FIXED 2026-08-05: this always read `filters?.territory?.id` - the top
// FilterBar's Territory multiselect - which is normally empty, so the map
// almost never had a territory_id to fetch with ("always empty"). The real
// region-osm-view.component.ts is driven by `selectedTerritoryId`, which is
// only ever set by expanding a territory's accordion row in the Cost Band
// Detail table (toggleAccordion(item) -> this.selectedTerritoryId =
// item.territoryId). That state lives in CostBandTable.jsx (a sibling
// component here, not a parent), so App.js now lifts it via CostBandTable's
// onTerritoryExpand callback and passes it down to this component as the
// `territoryId` prop below - not part of `filters` at all.
export default function RegionMap({ filters, territoryId }) {
  const [regionData, setRegionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const boundsRef = useRef(null);
  const averagesRef = useRef({ fullL1: 0, fullL2: 0, fullL3: 0 });

  const [heatmapEnabled, setHeatmapEnabled] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState("fullL1");
  const [hasValidData, setHasValidData] = useState(false);
  const [popup, setPopup] = useState(null); // { regionName, coords, fullL1, fullL2, fullL3 }
  const [popupStyle, setPopupStyle] = useState({ top: "0px", left: "0px" });
  const [multipleFeatures, setMultipleFeatures] = useState([]);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);

  const jobTitleId = filters?.jobTitle?.id;
  const timePeriodId = filters?.quarter?.id;

  // Same route CostBandTable.jsx's region accordion already calls - ports
  // GetTerritoryRegionSource.sql, already returns latitude/longitude.
  useEffect(() => {
    if (!jobTitleId || !territoryId || !timePeriodId) {
      setRegionData([]);
      return;
    }
    setLoading(true);
    const params = new URLSearchParams({
      job_title_id: jobTitleId,
      territory_id: territoryId,
      time_period_id: timePeriodId,
    });
    fetch(`/api/territory-region-source?${params.toString()}`)
      .then((res) => res.json())
      .then((rows) => {
        setRegionData(rows);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [jobTitleId, territoryId, timePeriodId]);

  const getCurrentFeatures = useCallback(() => {
    if (!regionData.length) return [];
    const bounds = new maplibregl.LngLatBounds();
    const features = regionData.map(toFeature).filter(Boolean);
    features.forEach((f) => bounds.extend(f.geometry.coordinates));
    boundsRef.current = bounds;
    return features;
  }, [regionData]);

  const calculateAverages = useCallback(() => {
    const metrics = ["fullL1", "fullL2", "fullL3"];
    const sums = { fullL1: 0, fullL2: 0, fullL3: 0 };
    const counts = { fullL1: 0, fullL2: 0, fullL3: 0 };
    regionData.forEach((item) => {
      metrics.forEach((m) => {
        const raw = m === "fullL1" ? item.full_l1 : m === "fullL2" ? item.full_l2 : item.full_l3;
        const v = parseFloat(raw);
        if (!Number.isNaN(v)) {
          sums[m] += v;
          counts[m]++;
        }
      });
    });
    averagesRef.current = {
      fullL1: counts.fullL1 > 0 ? sums.fullL1 / counts.fullL1 : 0,
      fullL2: counts.fullL2 > 0 ? sums.fullL2 / counts.fullL2 : 0,
      fullL3: counts.fullL3 > 0 ? sums.fullL3 / counts.fullL3 : 0,
    };
  }, [regionData]);

  const weightFor = (props, metric) => {
    const v = parseFloat(props[metric]) || 0;
    return Math.min(Math.max(v / 1000, 0.1), 1);
  };
  const comparisonFor = (props, metric) => {
    const v = parseFloat(props[metric]) || 0;
    const avg = averagesRef.current[metric];
    if (avg === 0) return v > 0 ? 1 : -1;
    return v >= avg ? 1 : -1;
  };

  // --- Map init, once ---
  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return;

    // Style is built here in JS rather than fetched, so nothing about the
    // base map depends on a network round trip we can't see failing: no
    // external host, no style.json fetch, no TileJSON indirection, no glyph
    // files (the two label layers that needed them are dropped - the map
    // shows country shapes, not names). Tile URLs are made absolute against
    // the page's own origin so they resolve identically on localhost:3000
    // and 10.20.3.31:3000. Tiles live in public/maptiles/tiles/.
    const style = {
      ...baseMapStyle,
      sources: {
        ...baseMapStyle.sources,
        maplibre: {
          ...baseMapStyle.sources.maplibre,
          tiles: baseMapStyle.sources.maplibre.tiles.map((t) =>
            t.replace("__ORIGIN__", window.location.origin)
          ),
        },
      },
    };

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      // SELF-HOSTED (2026-08-20): was https://demotiles.maplibre.org/style.json
      // - the same external URL old Minerva uses. That made the map's base
      // layer depend on each viewer's own internet/firewall path to that
      // site, which is exactly why it rendered for some viewers and not
      // others (worked on machines with it cached under the old app's
      // origin; blank where the fresh fetch was blocked - browser caches
      // are partitioned per top-level site, so old-app cache doesn't help
      // this origin). The full demotiles asset set (style + world vector
      // tiles + Latin glyph ranges of its 5 fonts, ~12MB) now ships in
      // public/maptiles/ with all internal URLs rewritten to /maptiles/...,
      // so the map needs nothing but this app's own server. Non-Latin glyph
      // ranges were dropped for size; Latin covers the country labels.
      style,
      center: [0, 0],
      zoom: 2,
      minZoom: 1,
      maxZoom: 5,
      attributionControl: { compact: true },
      renderWorldCopies: true,
    });
    map.addControl(new maplibregl.NavigationControl());

    // Ported from region-osm-view.component.ts's addCenterControl(). Uses
    // our existing boundsRef (kept up to date by getCurrentFeatures() below)
    // instead of the old code's `(map as any).bounds` - same behavior:
    // re-fit to the current markers, or recenter on the world if there's
    // nothing to fit to yet.
    class CenterControl {
      onAdd(mapInstance) {
        this._map = mapInstance;
        this._container = document.createElement("div");
        this._container.className = "maplibregl-ctrl maplibregl-ctrl-group";
        const button = document.createElement("button");
        button.type = "button";
        button.title = "Center Map";
        button.innerHTML = "⤾";
        button.style.fontSize = "16px";
        button.style.fontWeight = "bold";
        button.onclick = () => {
          const bounds = boundsRef.current;
          if (bounds && !bounds.isEmpty()) {
            mapInstance.fitBounds(bounds, { padding: { top: 80, bottom: 80, left: 80, right: 80 }, maxZoom: 5, duration: 1000 });
          } else {
            mapInstance.setCenter([0, 0]);
            mapInstance.setZoom(2);
          }
        };
        this._container.appendChild(button);
        return this._container;
      }
      onRemove() {
        this._container.parentNode?.removeChild(this._container);
        this._map = undefined;
      }
    }
    map.addControl(new CenterControl(), "top-right");

    map.on("load", () => {
      map.addSource("region-points", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({
        id: "region-markers",
        type: "circle",
        source: "region-points",
        paint: {
          "circle-radius": 8,
          "circle-color": "#007bff",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
        },
      });

      map.addSource("heatmap-source", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({
        id: "heatmap-layer",
        type: "circle",
        source: "heatmap-source",
        layout: { visibility: "none" },
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 0, 30, 5, 60],
          "circle-color": ["case", [">", ["get", "comparison"], 0], "rgba(40,167,69,0.7)", "rgba(220,53,69,0.7)"],
          "circle-blur": 1,
          "circle-opacity": 0.7,
        },
      });

      map.on("mouseenter", "region-markers", () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", "region-markers", () => { map.getCanvas().style.cursor = ""; });

      map.on("click", "region-markers", (e) => {
        if (!e.features || e.features.length === 0) return;
        const clickedCoords = e.features[0].geometry.coordinates.slice();
        const tolerance = 0.0001;
        const matches = e.features.filter((f) => {
          const c = f.geometry.coordinates;
          return Math.abs(c[0] - clickedCoords[0]) < tolerance && Math.abs(c[1] - clickedCoords[1]) < tolerance;
        });
        setMultipleFeatures(matches);
        setCurrentFeatureIndex(0);
        map.easeTo({ center: clickedCoords, zoom: Math.min(Math.max(map.getZoom(), 4), 5), duration: 800 });
        const pixel = map.project(clickedCoords);
        setPopupStyle({ left: `${pixel.x}px`, top: `${pixel.y - 10}px` });
        const props = matches[0].properties;
        setPopup({ regionName: props.regionName, coords: clickedCoords, fullL1: props.fullL1, fullL2: props.fullL2, fullL3: props.fullL3 });
      });

      map.on("move", () => {
        setPopup((p) => {
          if (!p) return p;
          const pixel = map.project(p.coords);
          setPopupStyle({ left: `${pixel.x}px`, top: `${pixel.y - 10}px` });
          return p;
        });
      });
    });

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // --- Update markers whenever regionData changes ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const doUpdate = () => {
      const features = getCurrentFeatures();
      setHasValidData(features.length > 0);

      if (features.length === 0) {
        setHeatmapEnabled(false);
        const src = map.getSource("region-points");
        const hsrc = map.getSource("heatmap-source");
        if (src) src.setData({ type: "FeatureCollection", features: [] });
        if (hsrc) hsrc.setData({ type: "FeatureCollection", features: [] });
        setPopup(null);
        if (!regionData.length) {
          map.setCenter([0, 0]);
          map.setZoom(2, { duration: 1000 });
        }
        return;
      }

      calculateAverages();

      const src = map.getSource("region-points");
      if (src) src.setData({ type: "FeatureCollection", features });

      const heatmapFeatures = features.map((f) => ({
        ...f,
        properties: {
          ...f.properties,
          weight: weightFor(f.properties, selectedMetric),
          comparison: comparisonFor(f.properties, selectedMetric),
        },
      }));
      const hsrc = map.getSource("heatmap-source");
      if (hsrc) hsrc.setData({ type: "FeatureCollection", features: heatmapFeatures });

      if (boundsRef.current && !boundsRef.current.isEmpty()) {
        map.fitBounds(boundsRef.current, { padding: { top: 80, bottom: 80, left: 80, right: 80 }, maxZoom: 5, duration: 1000 });
      }
    };

    if (map.isStyleLoaded()) doUpdate();
    else map.once("load", doUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionData]);

  // --- Heatmap toggle / metric change ---
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer("heatmap-layer")) return;
    map.setLayoutProperty("heatmap-layer", "visibility", heatmapEnabled ? "visible" : "none");

    const markerStyle = heatmapEnabled
      ? { radius: 4, color: "#ffffff", strokeColor: "#333333", strokeWidth: 1, opacity: 0.8 }
      : { radius: 8, color: "#007bff", strokeColor: "#ffffff", strokeWidth: 2, opacity: 1 };
    map.setPaintProperty("region-markers", "circle-radius", markerStyle.radius);
    map.setPaintProperty("region-markers", "circle-color", markerStyle.color);
    map.setPaintProperty("region-markers", "circle-stroke-color", markerStyle.strokeColor);
    map.setPaintProperty("region-markers", "circle-stroke-width", markerStyle.strokeWidth);
    map.setPaintProperty("region-markers", "circle-opacity", markerStyle.opacity);

    if (heatmapEnabled && hasValidData) {
      const features = getCurrentFeatures();
      calculateAverages();
      const heatmapFeatures = features.map((f) => ({
        ...f,
        properties: { ...f.properties, weight: weightFor(f.properties, selectedMetric), comparison: comparisonFor(f.properties, selectedMetric) },
      }));
      const hsrc = map.getSource("heatmap-source");
      if (hsrc) hsrc.setData({ type: "FeatureCollection", features: heatmapFeatures });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heatmapEnabled, selectedMetric]);

  useEffect(() => {
    if (multipleFeatures.length === 0) return;
    const props = multipleFeatures[currentFeatureIndex].properties;
    setPopup((p) => (p ? { ...p, regionName: props.regionName, fullL1: props.fullL1, fullL2: props.fullL2, fullL3: props.fullL3 } : p));
  }, [currentFeatureIndex, multipleFeatures]);

  return (
    <section className="rm-section">
      <div className="rm-panel">
        <div className="rm-title">Region Map</div>
        <div className="rm-map-container">
          <div ref={mapContainerRef} className="rm-map" />

          {!hasValidData && (
            <div className="rm-empty-overlay">
              <div className="rm-empty-title">No Region Data Available</div>
              <div className="rm-empty-sub">
                {loading
                  ? "Loading…"
                  : territoryId
                  ? "No region data found for this territory."
                  : "Expand a territory's row in the Cost Band Detail table above to view its regions."}
              </div>
            </div>
          )}

          {hasValidData && (
            <div className="rm-controls">
              <div className="rm-control-row">
                <span className="rm-control-label">Heatmap</span>
                <label className="rm-toggle">
                  <input type="checkbox" checked={heatmapEnabled} onChange={() => setHeatmapEnabled((v) => !v)} />
                  <span className={`rm-toggle-slider${heatmapEnabled ? " on" : ""}`}>
                    <span className={`rm-toggle-knob${heatmapEnabled ? " on" : ""}`} />
                  </span>
                </label>
              </div>
              {heatmapEnabled && (
                <div className="rm-control-row">
                  <span className="rm-control-label">Metric:</span>
                  <select className="rm-metric-select" value={selectedMetric} onChange={(e) => setSelectedMetric(e.target.value)}>
                    {METRIC_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {heatmapEnabled && hasValidData && (
            <div className="rm-legend">
              <div className="rm-legend-title">{selectedMetric.toUpperCase()} vs Average</div>
              <div className="rm-legend-row">
                <span className="rm-legend-swatch" style={{ background: "rgba(40,167,69,0.7)" }} />
                <span>Above Avg</span>
                <span className="rm-legend-swatch" style={{ background: "rgba(220,53,69,0.7)" }} />
                <span>Below Avg</span>
              </div>
            </div>
          )}

          {popup && (
            <div className="rm-popup" style={{ top: popupStyle.top, left: popupStyle.left }}>
              <button type="button" className="rm-popup-close" onClick={() => setPopup(null)}>&times;</button>
              {multipleFeatures.length > 1 && (
                <div className="rm-popup-nav">
                  <button type="button" disabled={currentFeatureIndex === 0} onClick={() => setCurrentFeatureIndex((i) => i - 1)}>‹</button>
                  <button type="button" disabled={currentFeatureIndex === multipleFeatures.length - 1} onClick={() => setCurrentFeatureIndex((i) => i + 1)}>›</button>
                </div>
              )}
              <div className="rm-popup-heading">
                Region Details
                {multipleFeatures.length > 1 && (
                  <span className="rm-popup-count">({currentFeatureIndex + 1} of {multipleFeatures.length})</span>
                )}
              </div>
              <table className="rm-popup-table">
                <tbody>
                  <tr><td className="rm-popup-key">Region</td><td className="rm-popup-val">{popup.regionName}</td></tr>
                  {popup.fullL1 != null && <tr><td className="rm-popup-key">New to Role (NTR)</td><td className="rm-popup-val">{fmtGBP(popup.fullL1)}</td></tr>}
                  {popup.fullL2 != null && <tr><td className="rm-popup-key">Experienced (Exp)</td><td className="rm-popup-val">{fmtGBP(popup.fullL2)}</td></tr>}
                  {popup.fullL3 != null && <tr><td className="rm-popup-key">V-Experienced (Vexp)</td><td className="rm-popup-val">{fmtGBP(popup.fullL3)}</td></tr>}
                  <tr><td className="rm-popup-key">Coordinates</td><td className="rm-popup-val">{popup.coords[0].toFixed(6)}, {popup.coords[1].toFixed(6)}</td></tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
